"use client";
import React, { useRef, useEffect, useState } from "react";
import { POST_REQUEST } from "@/utils/fetch";
import { useSession } from "next-auth/react";
import { ChatMessage, User, UserSession } from "@/utils/types";
import moment from "moment/moment";
import "moment/locale/fr";
import "moment/locale/ar";
moment.locale("fr");
import { Avatar } from "@mui/material";
import { FiSend } from "react-icons/fi";
import { ConflictNewMsgEmail } from "@/utils/email";
import CircularProgress from "@mui/material/CircularProgress";
import { UploadSpot } from "@/components/FileUpload";
import { CustomImage } from "@/components/CustomImage";
import { useTranslation } from "react-i18next";
import { ChatBotSuggestions } from "./ChatBotSuggestions";
import { availableName, sendNotification } from "@/utils/functions"; 
import { IconButton } from "@mui/material";
import { RiRobot2Line } from "react-icons/ri";
import { useQuery } from "@tanstack/react-query";
import api from "@/app/lib/api";
import { toast } from "react-toastify";
import { getSocket } from "@/utils/sharedUtils";

interface Message {
  roomId: string | number;
  user: User;
  message: string;
  image: string;
  date: string;
  user_id: number;
  sender_id: number | string;
}

const ChatDrawer = ({
  id,
  handleSuccess,
  isConflict,
  user,
}: {
  id: number | string;
  handleSuccess?: Function;
  isConflict: 0 | 1;
  user?: User | null;
}) => {
  const session = useSession().data as UserSession;
  const { t, i18n } = useTranslation();
  const currentLocale = i18n.language as "ar" | "fr" | "en";
  moment.locale(currentLocale);

  const socket = getSocket();

  const [msgData, setMsgData] = useState({ message: "", image: " " });
  const [chattingWithBot, setChatWithbot] = useState(true);

  // Join socket room on mount
  if (socket) socket.emit("join_room", { roomId: `${id}`, id: "chatDrawer" });

  // Fetch chat messages
  const {
    isLoading,
    error,
    isSuccess,
    data: chat,
    refetch,
  } = useQuery({
    queryKey: [
      "chat",
      session?.role == "admin" || session?.role == "super-admin"
        ? "admin"
        : id,
      id,
    ],
    queryFn: async () => api.chats.getOne(id as number, !!session),
    enabled: !!id,
  });

  // Function to send message (or bot reply)
  const sendData = async ({
    sender_id,
    message,
    send_notif,
  }: {
    sender_id?: number | string;
    message?: string;
    send_notif?: boolean;
  }) => {
    // Prevent sending if message and image are both empty, and no external message is passed
    if (
      (!msgData.message || msgData.message == " ") &&
      (!msgData.image || msgData.image == " ") &&
      !message
    )
      return;

    try {
      // Construct the message payload to be stored and emitted
      const msg = {
        chat_id: `${id}`, // Unique ID of the chat thread
        role: session?.role, // Sender's role (admin, user, etc.)
        sender_id: sender_id == 0 ? sender_id : session?.id ?? null, // If 0 (bot message), keep as is; otherwise use session ID
        msg: message ?? msgData.message, // message for bot response, else use typed message
        user_id: isConflict == 1 ? chat?.[0]?.user_id : id, // In conflict case, bind to actual user, else use passed Id (user)
        image: msgData.image ?? "", // Optional image attached to the message
        isConflict, // Flag indicating if this chat is a conflict discussion
      };

      // Store the message in the database
      await api.chats.insert(msg);

      refetch();
      handleSuccess?.();

      // Reset message and image input fields
      setMsgData({ message: "", image: " " });

      // Determine the type of notification to send with the socket event
      const newNotif =
        send_notif == undefined
          ? isConflict
            ? "conflicts"
            : "chats"
          : send_notif
          ? "chats"
          : "";

      // Emit the message through WebSocket
      await socket.emit("send_msg", {
        roomId:
          session?.role !== "admin" && session?.role !== "super-admin"
            ? ["admin", `${id}`] // Non-admin sends to both admin and user
            : [`${id}`], // Admin sends only to the user
        new: newNotif,
        is_admin: session?.role == "admin" || session?.role == "super-admin",
        ...msg, // include full message content
      });

      // Push notification to user's device (if applicable)
      sendNotification(
        `${session?.first_name} ${session?.last_name}`, // Sender's name
        msgData.message, // Message content
        `${process.env.NEXT_PUBLIC_BASE_URL}/api/images/uploads/${session?.profile_pic}`, // Avatar
        "/", // Target URL
        chat?.find((c) => c?.sender_id !== session?.id && c?.sender_id !== 0)
          ?.user?.token ?? "" // Recipient's device token
      );

      socket.disconnect();

      // Determine the receiver of the message
      let receiver = chat?.find((c) => c?.user?.id !== session?.id)?.user;

      // If no receiver found in chat (e.g. first bot message), fallback to fetching user info
      if (!receiver) {
        const user = await api.users.getOne(session?.id!);
        receiver = user;
      }

      // If this is a conflict-related message, send an email notification
      if (isConflict) {
        // Generate HTML body for conflict email
        const htmlMessageBody = ConflictNewMsgEmail({
          user: receiver!,
          conflict: id as number,
          msg: msgData.message,
        });

        // Send the email to the receiver
        await POST_REQUEST("send_email", {
          receiver: receiver?.email,
          subject: "Nouveau message reçu",
          message: htmlMessageBody,
        });
      }
    } catch (error: any) {
      // Display error toast and log the error
      toast.error(`${t("error")} : ${error?.message}`, {
        position: "top-center",
      });
      console.error("Error sending message:", error);
    }
  };

  // On mount: send welcome message if no messages today
  useEffect(() => {
    const message = {
      en: "Hi there! How can I assist you today?",
      fr: "Salut! Comment puis-je vous aider aujourd'hui?",
      ar: "مرحبًا! كيف يمكنني مساعدتك اليوم؟",
    };

    const isClient =
      session?.role !== "admin" && session?.role !== "super-admin";
    const isNoMessageToday =
      chat &&
      chat.find((c) => moment(`${c?.date}`).isSame(moment(), "day")) ==
        undefined;

    if (chat && !isConflict && isClient && isNoMessageToday)
      sendData({
        sender_id: 0,
        message: availableName(message, currentLocale),
      });
  }, [chat]);

  // Listen for new messages
  useEffect(() => {
    socket.on("receive_msg", () => {
      refetch();
    });
  }, [socket]);

  // Show error if chat fails to load
  if (error)
    return (
      <div className="grid center h100 w100">
        <img src="/error.png" alt="" className="h70px" />
        <p className="dark-grey f15">
          {t("error")} : {error?.message ?? JSON.stringify(error)}
        </p>
      </div>
    );

  return (
    <div className="chat-drawer">
      {/* Spacer for admin header */}
      {(session?.role == "admin" || session?.role == "super-admin") && (
        <div className="h50px"></div>
      )}

      {/* Bot icon toggle */}
      {!chattingWithBot && (
        <div className="action right">
          <IconButton
            size="small"
            onClick={() => setChatWithbot(true)}
            color="primary"
          >
            <RiRobot2Line />
          </IconButton>
        </div>
      )}

      {/* Loading spinner */}
      {isLoading && (
        <div className="c1 center">
          <CircularProgress />
        </div>
      )}

      {/* Chat messages */}
      {isSuccess && (
        <div className="chat-content h100 scroll">
          {chat?.map(({ user, message, image, date, sender_id }, key) => (
            <div
              key={key}
              className={
                sender_id == session?.id || (!session && sender_id == id)
                  ? "message-container right"
                  : "message-container left"
              }
            >
              <div></div>
              <div>
                <Avatar
                  src={
                    user?.profile_pic
                      ? `/api/images/uploads/${user?.profile_pic}`
                      : sender_id == 0
                      ? "/bot.png"
                      : "/placeholder.jpeg"
                  }
                />
                <p className="f13 dark-grey">
                  {(user as User)?.role == "user"
                    ? user?.first_name
                    : sender_id !== 0
                    ? "Unknow"
                    : "ChatBot"}
                </p>
                <p className="f13 dark-grey">{user?.last_name}</p>
              </div>
              <div>
                {message && message !== " " && (
                  <div className="pb10">
                    <div className="space-between">
                      {(sender_id == session?.id ||
                        (!session && sender_id == id)) && <div></div>}
                      <p className="f12 dark-grey">
                        {moment(`${date}`).calendar()}
                      </p>
                    </div>
                    <div
                      className={
                        sender_id == session?.id ||
                        (!session && sender_id == id)
                          ? "message right"
                          : "message left"
                      }
                    >
                      {message}
                    </div>
                  </div>
                )}
                {image && image !== " " && (
                  <CustomImage
                    src={image}
                    alt="user-image"
                    className="chat-image"
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Spacer for input */}
      {(session?.role == "admin" || session?.role == "super-admin") && (
        <div className="h100px"></div>
      )}

      {/* Input + Suggestions */}
      <div className="inputs-container">
        {/* Show bot suggestions only for clients */}
        {session?.role !== "admin" &&
          session?.role !== "super-admin" &&
          chattingWithBot &&
          !isConflict && (
            <div>
              <ChatBotSuggestions
                quest={[]}
                setQuestion={async ({ question, send_notif }) => {
                  await sendData({
                    message: question,
                    send_notif,
                    sender_id: session?.id ?? id,
                  });
                  refetch();
                }}
                setReponse={async (response: string) => {
                  setTimeout(async () => {
                    await sendData({ sender_id: 0, message: response });
                    refetch();
                  }, 1000);
                }}
              />
            </div>
          )}

        {/* Message input form */}
        <form
          className="c1-auto2 first-baseline gap10"
          onSubmit={async (e) => {
            e.preventDefault();
            await sendData({ sender_id: session?.id ?? id });

            // Auto bot response fallback
            setTimeout(() => {
              if (
                chattingWithBot &&
                session?.role !== "admin" &&
                session?.role !== "super-admin" &&
                !isConflict
              ) {
                sendData({
                  sender_id: 0,
                  message: "I didn't understand your request",
                });
              }
            }, 1000);
          }}
        >
          <div className="light-grey-bg br15 c1-auto gap10 p5">
            <input
              className="h100"
              type="text"
              required
              value={msgData.message}
              placeholder={t("yourMessage")}
              onChange={(e) =>
                setMsgData({ ...msgData, message: e.target.value })
              }
            />
            {msgData.image !== " " && (
              <CustomImage
                src={msgData.image}
                alt="Selected preview"
                className="h60px w60px br15 light-grey-bg"
              />
            )}
          </div>

          {/* Image upload */}
          <UploadSpot
            isButton
            value={msgData.image}
            onChange={(newFile) =>
              setMsgData({ ...msgData, image: newFile?.url || "" })
            }
          />

          {/* Submit button */}
          <button
            className="chat-button"
            type="submit"
            style={{
              backgroundColor:
                (!msgData.message || msgData.message == " ") &&
                (!msgData.image || msgData.image == " ")
                  ? "#536A92"
                  : "#16489f",
            }}
          >
            <FiSend />
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatDrawer;
